/**
 * AdminEmployerVerification — iter-135
 *
 * Two-pane layout: left is the queue list (pending / info_requested /
 * suspended by default, or any status via filter chip). Right is the
 * detail panel with the six-item checklist and the four action buttons
 * (Approve / Request info / Reject / Suspend).
 *
 * Roles:
 *  - platform_admin: everything
 *  - support_admin: checklist edits only (action buttons disabled)
 *  - compliance_admin: read-only
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    ShieldCheck, AlertTriangle, CheckCircle2, XCircle, Loader2, Search,
    ChevronRight, Building2, Clock, Info,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAdminContext } from "@/pages/admin/AdminShell";

const STATUS_CHIPS = [
    { key: "queue", label: "Queue (pending + info + suspended)" },
    { key: "pending", label: "Pending" },
    { key: "info_requested", label: "Info requested" },
    { key: "approved", label: "Approved" },
    { key: "rejected", label: "Rejected" },
    { key: "suspended", label: "Suspended" },
];

const CHECKLIST_META = [
    { key: "sos_lookup", label: "Secretary of State lookup" },
    { key: "domain_website", label: "Domain / website check" },
    { key: "video_call", label: "Live video call" },
    { key: "footprint", label: "Online footprint" },
    { key: "phone_verified", label: "Business phone verified" },
    { key: "payment_method", label: "Verifiable payment method" },
];

const RESULT_OPTS = [
    { v: "pass", label: "Pass",  cls: "bg-emerald-600 text-white" },
    { v: "flag", label: "Flag",  cls: "bg-amber-500 text-white" },
    { v: "fail", label: "Fail",  cls: "bg-rose-600 text-white" },
    { v: "na",   label: "N/A",   cls: "bg-slate-500 text-white" },
];

const StatusBadge = ({ s }) => {
    const map = {
        pending: "bg-slate-100 text-slate-800",
        info_requested: "bg-amber-100 text-amber-800",
        approved: "bg-emerald-100 text-emerald-800",
        rejected: "bg-rose-100 text-rose-800",
        suspended: "bg-orange-100 text-orange-800",
    };
    return (
        <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${map[s] || "bg-slate-100"}`}>
            {s || "—"}
        </span>
    );
};

export default function AdminEmployerVerification() {
    const ctx = useAdminContext();
    const me = ctx?.me;
    const roles = new Set([me?.admin_role, ...((me?.admin_roles) || [])].filter(Boolean));
    const canWrite = roles.has("platform_admin");
    const canEditChecklist = canWrite || roles.has("support_admin");

    const [filter, setFilter] = useState("queue");
    const [list, setList] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [busy, setBusy] = useState(false);
    const [suspendDialog, setSuspendDialog] = useState({ open: false, template: "", reason: "" });

    const fetchList = useCallback(async () => {
        try {
            const url = filter === "queue"
                ? "/admin/employers/verification"
                : `/admin/employers/verification?status=${filter}`;
            const { data } = await api.get(url);
            setList(data.results || []);
            if (!selectedId && (data.results || []).length) {
                setSelectedId(data.results[0].employer_id);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load queue");
        }
    }, [filter, selectedId]);

    const fetchDetail = useCallback(async (id) => {
        if (!id) { setDetail(null); return; }
        try {
            const { data } = await api.get(`/admin/employers/verification/${id}`);
            setDetail(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load detail");
        }
    }, []);

    useEffect(() => { fetchList(); }, [fetchList]);
    useEffect(() => { fetchDetail(selectedId); }, [selectedId, fetchDetail]);

    const checklistComplete = useMemo(() => {
        if (!detail?.verification?.checklist) return false;
        return detail.verification.checklist.every((c) => c.result);
    }, [detail]);

    const setChecklistResult = async (key, result) => {
        if (!canEditChecklist) return;
        setBusy(true);
        try {
            await api.patch(
                `/admin/employers/verification/${selectedId}/checklist`,
                { key, result, note: "" }
            );
            await fetchDetail(selectedId);
            await fetchList();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Update failed");
        } finally { setBusy(false); }
    };

    const doAction = async (action, promptReason = false) => {
        if (!canWrite) return;
        // Suspend has a preset-reason picker instead of a bare prompt.
        if (action === "suspend") {
            setSuspendDialog({ open: true, template: "", reason: "" });
            return;
        }
        let reason = "";
        if (promptReason) {
            reason = window.prompt(`Reason for ${action}?`) || "";
            if (!reason.trim() && action === "reject") {
                toast.error("A reason is required.");
                return;
            }
        }
        if (action === "approve" && !checklistComplete) {
            toast.error("Every checklist item must be marked before approval.");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/admin/employers/verification/${selectedId}/${action}`, { reason });
            toast.success(`Employer marked ${action.replace("-", " ")}.`);
            await fetchDetail(selectedId);
            await fetchList();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Action failed");
        } finally { setBusy(false); }
    };

    const submitSuspend = async () => {
        const reason = (suspendDialog.reason || "").trim() || suspendDialog.template;
        if (!reason) {
            toast.error("Pick a preset or write a reason.");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/admin/employers/verification/${selectedId}/suspend`, { reason });
            toast.success("Employer suspended.");
            setSuspendDialog({ open: false, template: "", reason: "" });
            await fetchDetail(selectedId);
            await fetchList();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Action failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-background text-foreground" data-testid="admin-employer-verification-page">
            <div className="max-w-7xl mx-auto p-6">
                <header className="mb-6">
                    <div className="inline-flex items-center gap-2 text-xs uppercase font-semibold text-emerald-700 dark:text-emerald-400">
                        <ShieldCheck size={14} /> Trust &amp; Safety
                    </div>
                    <h1 className="mt-1 font-heading text-3xl font-semibold">Employer Verification Queue</h1>
                    <p className="text-muted-foreground text-sm">
                        Approve real, legitimate businesses. Tier-1 employers' first 3 postings still route through admin approval; tier-2 posts land live.
                    </p>
                </header>

                <div className="flex flex-wrap gap-2 mb-4">
                    {STATUS_CHIPS.map((c) => (
                        <button
                            key={c.key}
                            onClick={() => { setFilter(c.key); setSelectedId(null); }}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                                filter === c.key
                                    ? "bg-foreground text-background border-foreground"
                                    : "bg-background border-border hover:bg-muted"
                            }`}
                            data-testid={`ev-filter-${c.key}`}
                        >{c.label}</button>
                    ))}
                    <button
                        onClick={fetchList}
                        className="px-3 py-1.5 rounded-full text-xs border border-border hover:bg-muted"
                        data-testid="ev-refresh"
                    >Refresh</button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Queue */}
                    <div className="lg:col-span-1 rounded-2xl border border-border bg-card overflow-hidden" data-testid="ev-queue">
                        <div className="p-3 border-b border-border text-xs text-muted-foreground">
                            {list.length} row{list.length === 1 ? "" : "s"}
                        </div>
                        <ul className="max-h-[70vh] overflow-y-auto divide-y divide-border">
                            {list.length === 0 && (
                                <li className="p-6 text-center text-sm text-muted-foreground">
                                    Queue is empty. Nice work.
                                </li>
                            )}
                            {list.map((row) => (
                                <li
                                    key={row.employer_id}
                                    onClick={() => setSelectedId(row.employer_id)}
                                    className={`p-3 cursor-pointer hover:bg-muted/50 flex items-center gap-3 ${
                                        selectedId === row.employer_id ? "bg-muted" : ""
                                    }`}
                                    data-testid={`ev-row-${row.employer_id}`}
                                >
                                    <Building2 size={16} className="text-muted-foreground shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-semibold truncate">
                                            {row.business_name || row.contact_email || row.employer_id}
                                        </div>
                                        <div className="text-[11px] text-muted-foreground flex items-center gap-2">
                                            <StatusBadge s={row.status} />
                                            {row.state_of_formation && <span>{row.state_of_formation}</span>}
                                            {row.days_waiting != null && (
                                                <span className="inline-flex items-center gap-1">
                                                    <Clock size={11} /> {row.days_waiting}d
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <ChevronRight size={16} className="text-muted-foreground" />
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Detail */}
                    <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-5" data-testid="ev-detail">
                        {!detail && (
                            <div className="py-10 text-center text-sm text-muted-foreground">
                                Select an employer from the queue.
                            </div>
                        )}
                        {detail && (
                            <>
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h2 className="font-heading text-xl font-semibold">
                                                {detail.verification.legal_business_name || detail.employer.company_name || detail.employer.email}
                                            </h2>
                                            <StatusBadge s={detail.verification.status} />
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-1">
                                            {detail.employer.email} · {detail.verification.domain || "no domain"} · signed up {detail.employer.created_at?.slice(0, 10)}
                                        </div>
                                        <div className="text-xs mt-1">
                                            Trust tier: <strong>{detail.verification.trust_tier || 0}</strong>
                                            {" · "}Open reports: <strong>{detail.open_reports}</strong>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-5">
                                    <h3 className="font-semibold text-sm mb-2">Manual checklist</h3>
                                    <ul className="space-y-2" data-testid="ev-checklist">
                                        {(detail.verification.checklist || []).map((item) => (
                                            <li key={item.key} className="rounded-lg border border-border p-3 flex items-center gap-3">
                                                <div className="flex-1">
                                                    <div className="text-sm font-medium">{item.label}</div>
                                                    <div className="text-[11px] text-muted-foreground">
                                                        {item.checked_by ? `Marked by ${item.checked_by}` : "Not marked yet"}
                                                    </div>
                                                </div>
                                                <div className="flex gap-1">
                                                    {RESULT_OPTS.map((r) => (
                                                        <button
                                                            key={r.v}
                                                            disabled={!canEditChecklist || busy}
                                                            onClick={() => setChecklistResult(item.key, r.v)}
                                                            className={`text-[10px] px-2 py-1 rounded-full font-semibold uppercase transition disabled:opacity-40 ${
                                                                item.result === r.v ? r.cls : "bg-muted text-foreground hover:bg-muted/70"
                                                            }`}
                                                            data-testid={`ev-checklist-${item.key}-${r.v}`}
                                                        >{r.label}</button>
                                                    ))}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {!canWrite && (
                                    <div className="mt-4 text-xs text-muted-foreground inline-flex items-center gap-1">
                                        <Info size={12} /> Approval / reject / suspend requires platform_admin.
                                    </div>
                                )}

                                <div className="mt-5 flex flex-wrap gap-2">
                                    <button
                                        onClick={() => doAction("approve")}
                                        disabled={!canWrite || busy || !checklistComplete || detail.verification.status === "approved"}
                                        className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-2 disabled:opacity-40"
                                        data-testid="ev-action-approve"
                                    ><CheckCircle2 size={14} /> Approve</button>
                                    <button
                                        onClick={() => doAction("request-info", true)}
                                        disabled={!canWrite || busy}
                                        className="inline-flex items-center gap-1.5 rounded-full border border-border text-xs font-semibold px-3 py-2 hover:bg-muted disabled:opacity-40"
                                        data-testid="ev-action-request-info"
                                    ><AlertTriangle size={14} /> Request info</button>
                                    <button
                                        onClick={() => doAction("reject", true)}
                                        disabled={!canWrite || busy || detail.verification.status === "rejected"}
                                        className="inline-flex items-center gap-1.5 rounded-full bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold px-3 py-2 disabled:opacity-40"
                                        data-testid="ev-action-reject"
                                    ><XCircle size={14} /> Reject</button>
                                    <button
                                        onClick={() => doAction("suspend", true)}
                                        disabled={!canWrite || busy || detail.verification.status !== "approved"}
                                        className="inline-flex items-center gap-1.5 rounded-full bg-orange-600 hover:bg-orange-700 text-white text-xs font-semibold px-3 py-2 disabled:opacity-40"
                                        data-testid="ev-action-suspend"
                                    ><AlertTriangle size={14} /> Suspend</button>
                                </div>

                                {detail.verification.reverify_due_at && (
                                    <p className="mt-3 text-[11px] text-muted-foreground">
                                        Reverify due: {detail.verification.reverify_due_at.slice(0, 10)}
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
            {suspendDialog.open && (
                <div className="fixed inset-0 z-[95] bg-black/60 flex items-center justify-center p-4" data-testid="ev-suspend-dialog">
                    <div className="w-full max-w-md rounded-2xl border border-border bg-background shadow-2xl p-5">
                        <div className="text-xs uppercase text-orange-700 dark:text-orange-400 font-semibold">Suspend employer</div>
                        <h3 className="font-heading text-lg font-semibold mt-1">Pick a preset reason (or write one below)</h3>
                        <div className="mt-4 space-y-2">
                            {[
                                { v: "payment_fraud",    label: "Payment fraud / chargeback abuse" },
                                { v: "harassment",       label: "Harassment reports against talent" },
                                { v: "misrepresentation", label: "Misrepresentation of business or role" },
                            ].map((opt) => (
                                <label key={opt.v} className={`flex items-center gap-2 rounded-lg border p-3 cursor-pointer text-sm ${
                                    suspendDialog.template === opt.v ? "border-orange-500 bg-orange-500/5" : "border-border hover:bg-muted/40"
                                }`}>
                                    <input
                                        type="radio" name="suspend-preset"
                                        checked={suspendDialog.template === opt.v}
                                        onChange={() => setSuspendDialog({ ...suspendDialog, template: opt.v, reason: opt.label })}
                                        data-testid={`ev-suspend-preset-${opt.v}`}
                                    />
                                    {opt.label}
                                </label>
                            ))}
                        </div>
                        <label className="block text-xs font-semibold mt-4 mb-1">Custom reason (overrides preset)</label>
                        <textarea
                            rows={3} maxLength={1000}
                            value={suspendDialog.reason}
                            onChange={(e) => setSuspendDialog({ ...suspendDialog, reason: e.target.value })}
                            placeholder="Optional — pick a preset above or type here"
                            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                            data-testid="ev-suspend-reason-text"
                        />
                        <div className="mt-4 flex justify-end gap-2">
                            <button
                                onClick={() => setSuspendDialog({ open: false, template: "", reason: "" })}
                                disabled={busy}
                                className="text-xs px-3 py-2 rounded-full border border-border hover:bg-muted"
                            >Cancel</button>
                            <button
                                onClick={submitSuspend} disabled={busy}
                                className="text-xs px-3 py-2 rounded-full bg-orange-600 hover:bg-orange-700 text-white font-semibold"
                                data-testid="ev-suspend-confirm"
                            >Confirm suspend</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
